import { captureException } from '@sentry/browser';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ConsentTermsKoreaTestId, TermsTypeKorea } from './ConsentTermsKorea';
import { TermsTypeMain } from './ConsentTermsMain';
import { RegistrationFormTestId } from './RegistrationForm';
import { SingleSignOnFormTestId } from './SingleSignOnForm';
import * as S from './styles';

export type TermBoxType = TermsTypeMain | TermsTypeKorea;

interface TermBoxInfo {
    termsInfoStart?: string | null | React.ReactNode;
    termsInfoEnd?: string | null;
    firstAnchorText?: string | null;
    secondAnchorText?: string | null;
}
interface Props {
    type?: TermBoxType | never;
    dataTestId: RegistrationFormTestId | ConsentTermsKoreaTestId | SingleSignOnFormTestId;
    onChange: () => void;
    isChecked: boolean;
    hideTosPrivacyUpdateMemberWeb?: boolean;
    shouldRender?: boolean;
    firstUrl?: string | null;
    secondUrl?: string | null;
}

export const ConsentTerms: React.FC<Props> = props => {
    const getTermBoxInfo = (
        termBoxType: TermBoxType,
        hideTosPrivacyUpdateMemberWeb: boolean,
    ): TermBoxInfo => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { t } = useTranslation();

        switch (termBoxType) {
            case TermsTypeMain.TOS: {
                const termsInfoStartTos = hideTosPrivacyUpdateMemberWeb
                    ? t('auth:registration_form.oldTermsInfo')
                    : t('auth:registration_form.termsInfo');
                const firstAnchorTextTos = hideTosPrivacyUpdateMemberWeb
                    ? t('auth:registration_form.oldTermsName')
                    : t('auth:registration_form.termsName');

                return { termsInfoStart: termsInfoStartTos, firstAnchorText: firstAnchorTextTos };
            }
            case TermsTypeMain.PRIVACY_POLICY:
                return {
                    termsInfoStart: t('auth:registration_form.privacyInfo'),
                    firstAnchorText: t('auth:registration_form.privacyName'),
                };
            case TermsTypeMain.PRIVACY_POLICY_EURO:
                return {
                    termsInfoStart: t('auth:registration_form.privacyInfoEurope'),
                    termsInfoEnd: t('auth:registration_form.privacyInfoEndEurope'),
                    firstAnchorText: t('auth:registration_form.privacyNameEurope'),
                };
            case TermsTypeMain.PERSONAL_HEALTH_INFO:
                return {
                    termsInfoStart: t('auth:registration_form.personalHealthInfo'),
                };
            case TermsTypeKorea.ACCEPT_ALL:
                return {
                    termsInfoStart: (
                        <Trans
                            ns="auth"
                            i18nKey="registration_form.consentToAll.Korea"
                            values={{ text: t('auth:registration_form.consentToAll.strong.Korea') }}
                            // eslint-disable-next-line react/jsx-key
                            components={[<S.Strong />]}
                        />
                    ),
                };
            case TermsTypeKorea.TOS_KOREA:
                return {
                    termsInfoStart: t('auth:registration_form.privacyInfo.Korea'),
                    firstAnchorText: t('auth:registration_form.privacyName.Korea'),
                    secondAnchorText: t('auth:registration_form.privacyNameSecondary.Korea'),
                };
            case TermsTypeKorea.COLLECTION_AND_USE_OF_PERSONAL_INFO:
                return {
                    termsInfoStart: t('auth:registration_form.collection.useOfPersonalInfo.Korea'),
                    firstAnchorText: t(
                        'auth:registration_form.collection.useOfPersonalInfoName.Korea',
                    ),
                };
            case TermsTypeKorea.COLLECTION_AND_USE_OF_SENSITIVE_INFO:
                return {
                    termsInfoStart: t('auth:registration_form.collection.useOfSensitiveInfo.Korea'),
                    firstAnchorText: t(
                        'auth:registration_form.collection.useOfSensitiveInfoName.Korea',
                    ),
                };
            case TermsTypeKorea.PROVISION_OF_PERSONAL_INFO_TO_THIRD_PARTIES:
                return {
                    termsInfoStart: t('auth:registration_form.provisionPersonalInfo.Korea'),
                    firstAnchorText: t('auth:registration_form.provisionPersonalInfoName.Korea'),
                };
            case TermsTypeKorea.OVERSEAS_TRANSFER_OF_PERSONAL_INFO:
                return {
                    termsInfoStart: t('auth:registration_form.overseasPersonalInfoXferInfo.Korea'),
                    firstAnchorText: t(
                        'auth:registration_form.overseasPersonalInfoXferInfoName.Korea',
                    ),
                };
            case TermsTypeKorea.OPTIONAL_COLLECTION_AND_USE_OF_PERSONAL_INFO_FOR_MARKETING:
                return {
                    termsInfoStart: t(
                        'auth:registration_form.collection.personalInfoForMarketingInfo.Korea',
                    ),
                    firstAnchorText: t(
                        'auth:registration_form.collection.personalInfoForMarketingInfoName.Korea',
                    ),
                };
            default:
                const exhaustiveCheck: never = termBoxType;
                captureException(new Error(`Undefined Consent Term type used: ${exhaustiveCheck}`));
                return {
                    termsInfoStart,
                    termsInfoEnd,
                    firstAnchorText,
                    secondAnchorText,
                };
        }
    };

    const {
        type,
        dataTestId,
        onChange,
        isChecked,
        shouldRender = true,
        firstUrl,
        secondUrl,
    } = props;

    if (!type || !shouldRender) {
        return null;
    }

    const { termsInfoStart, termsInfoEnd, firstAnchorText, secondAnchorText } = getTermBoxInfo(
        type,
        props.hideTosPrivacyUpdateMemberWeb ?? false,
    );

    return (
        <S.TermsBox>
            <S.Checkbox data-testid={dataTestId} onChange={onChange} checked={isChecked} />
            <S.TermsInfo>
                {termsInfoStart}
                {firstUrl && (
                    <S.Anchor
                        data-testid={ConsentTermsKoreaTestId.FirstUrl}
                        onClick={() => window.open(firstUrl, '_blank')}
                    >
                        {firstAnchorText}
                    </S.Anchor>
                )}
                {secondUrl && (
                    <S.Anchor
                        data-testid={ConsentTermsKoreaTestId.SecondUrl}
                        style={{ display: 'inline-block' }}
                        onClick={() => window.open(secondUrl, '_blank')}
                    >
                        {secondAnchorText}
                    </S.Anchor>
                )}
                {termsInfoEnd}
            </S.TermsInfo>
        </S.TermsBox>
    );
};
